import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowContests, updateContestsData } from "@/Api/contests";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectProducts } from "@/Api/products";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { Mask } from "@/utils/mask";

const step1Schema = z.object({
  code: z.string({ message: "*requerido" }).min(1, "*requerido"),
  validation_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  prize: z.string().min(1, "*requerido"),
});

const step2Schema = z.object({
  lottery_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  product_classifications: z
    .array(z.string())
    .min(1, "*Selecione pelo menos um bolão"),
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
};

const schema = z.object({
  ...step1Schema.shape,
  ...step2Schema.shape,
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
  open,
  onOpenChange,
  hideTrigger,
}: {
  updateFront: (response: any) => void;
  uuid: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) => {
  const [lotteries, setLotteries] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [originalProducts, setOriginalProducts] = useState<any[]>([]);
  const stepControls = useStepControl();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      const [lotteriesResponse, contestResponse] = await Promise.all([
        getSelectProductGroups(),
        getShowContests(uuid),
      ]);

      setLotteries(lotteriesResponse.data?.response || []);

      const contest = contestResponse.data?.response;

      const productsResponse = await getSelectProducts({
        lottery_id: contest.lottery_id?.toString(),
      });
      setProducts(productsResponse.data?.response || []);
      if (contest) {
        form.setValue("code", contest?.name || "");
        form.setValue(
          "validation_date",
          contest.validation_date
            ? new Date(contest.validation_date).toISOString().split("T")[0]
            : ""
        );
        form.setValue("lottery_id", contest.lottery_id?.toString() || "");
        form.setValue("prize", Mask.formatCurrency(contest.prize || 0));

        const productClassifications = contest.product_classifications || [];
        setOriginalProducts(productClassifications);

        const productIds = productClassifications.map((pc: any) =>
          pc.product_classification_id.toString()
        );

        form.setValue("product_classifications", productIds);
      }
    };
    fetchData();
  }, [isOpen]);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
      validation_date: "",
      lottery_id: "",
      product_classifications: [],
      prize: "",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);
      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const addProduct = () => {
    const currentProducts = form.getValues("product_classifications");
    form.setValue("product_classifications", [...currentProducts, ""]);
  };

  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("product_classifications");
    const productToRemove = currentProducts[index];

    if (
      originalProducts.some(
        (p) => p.product_classification_id.toString() === productToRemove
      )
    ) {
      // @TODO ALERTA DE QUE O BOLÃO É OBRIGATÓRIO
    }

    currentProducts.splice(index, 1);
    form.setValue("product_classifications", currentProducts);
  };

  const getSelectedProductsDetails = () => {
    const selectedIds = form.getValues("product_classifications");
    const selectedProducts = selectedIds
      .map((id) => products.find((p) => p.id.toString() === id))
      .filter(Boolean);

    const totalGames = selectedProducts.reduce((sum, product) => {
      return sum + (product.game_quantity || 0);
    }, 0);

    const totalQuotas = selectedProducts.reduce((sum, product) => {
      return sum + (product.quota_quantity || 0);
    }, 0);

    const totalValue = selectedProducts.reduce((sum, product) => {
      const match = product.name.match(/R\$ (\d+[.,]\d+)/);
      return sum + (match ? parseFloat(match[1].replace(",", ".")) : 0);
    }, 0);

    const productCounts = selectedIds.reduce((acc, id) => {
      acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const productDetails = Object.entries(productCounts).map(([id, count]) => {
      const product = products.find((p) => p.id.toString() === id);
      return {
        name: product?.name || "Sem Produto",
        count,
      };
    });

    return { totalGames, totalQuotas, totalValue, productDetails };
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    try {
      const payload = {
        name: `${data.code.toString()}`,
        validation_date: data.validation_date,
        lottery_id: parseInt(data.lottery_id),
        prize: data.prize
          ? parseFloat(Mask.transformCurrencyInFloat(data.prize))
          : null,
        product_classifications: data.product_classifications,
      };

      const { data: apiResponse, ok } = await updateContestsData(uuid, payload);

      if (!ok) throw new Error();

      const payloadResponse = {
        ...apiResponse?.response,
        is_active: true,
      };

      updateFront(payloadResponse);
      stepControls.setStep(1);
      setFormState("default");
      form.reset();
      setIsOpen(false);
      toast({
        description: `Concurso atualizado com sucesso!`,
        action: <Check color="#1edc2b" />,
      });
    } catch (error) {
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
      toast({
        description: "Erro ao atualizar concurso",
        action: <Check color="#dc1e1e" />,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
            onClick={() => setIsOpen(true)}
          >
            <Pencil className="size-5" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[650px] h-auto">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Concurso
          </DialogDescription>
        </DialogHeader>

        <FormSteps controls={stepControls}>
          <FormSteps.Header>
            <FormSteps.Indicator step={1}>
              Informações Básicas
            </FormSteps.Indicator>
            <FormSteps.Indicator step={2}>Loteria e Bolões</FormSteps.Indicator>
          </FormSteps.Header>

          <ScrollArea className="h-[calc(90vh-10rem)] pr-4">
            <Form
              onSubmit={form.handleSubmit(formSubmit)}
              {...form}
              className="space-y-3 p-2"
            >
              <FormSteps.Content step={1}>
                <Form.Row>
                  <Form.Field name="code">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Concurso <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            type="number"
                            placeholder="Ex: 2455"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="validation_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Data de Validação <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="prize">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Prêmio <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="R$ 0,00"
                            typeMask="currency"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </FormSteps.Content>

              <FormSteps.Content step={2}>
                <Form.Row>
                  <Form.Field name="lottery_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Loteria <Form.Message />
                        </Form.Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma loteria" />
                          </SelectTrigger>
                          <SelectContent>
                            {lotteries.map((lottery) => (
                              <SelectItem
                                key={lottery.id}
                                value={lottery.id.toString()}
                              >
                                {lottery.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="product_classifications">
                    {({ field }) => (
                      <Form.Item className="w-full">
                        <Form.Label className="flex justify-between items-center">
                          <span>
                            Bolões <Form.Message />
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {field.value.length > 0 &&
                              `Bolões - ${field.value.length}`}
                          </span>
                        </Form.Label>
                        <div className="flex flex-col gap-4">
                          {field.value.map((_, index) => (
                            <div
                              key={index}
                              className="flex flex-col sm:flex-row gap-2 items-start sm:items-center"
                            >
                              <div className="flex-shrink-0 text-sm text-muted-foreground w-12">
                                B{index + 1}
                              </div>
                              <Select
                                value={field.value[index]}
                                onValueChange={(value) => {
                                  const newValues = [...field.value];
                                  newValues[index] = value;
                                  field.onChange(newValues);
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Selecione um bolão" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((product) => (
                                    <SelectItem
                                      key={product.id}
                                      value={product.id.toString()}
                                    >
                                      {product.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                type="button"
                                size="icon"
                                onClick={() => removeProduct(index)}
                                className="flex-shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addProduct}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Bolão
                          </Button>

                          {field.value.length > 0 && (
                            <div className="mt-4 p-4 bg-muted rounded-lg">
                              <h4 className="font-medium mb-2">Resumo</h4>
                              <div className="space-y-1 text-sm">
                                {(() => {
                                  const {
                                    totalGames,
                                    totalQuotas,
                                    totalValue,
                                    productDetails,
                                  } = getSelectedProductsDetails();
                                  return (
                                    <>
                                      <p>Total de Jogos: {totalGames}</p>
                                      <p>Total de Cotas: {totalQuotas}</p>
                                      <p>
                                        Valor Total:{" "}
                                        {Mask.formatCurrency(totalValue)}
                                      </p>
                                      <div className="mt-2">
                                        <p className="font-medium">
                                          Quantidade por bolão:
                                        </p>
                                        {productDetails.map(
                                          (product, index) => (
                                            <p key={index}>
                                              {product.count}x: {product.name}
                                            </p>
                                          )
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </FormSteps.Content>

              <DialogFooter className="sticky bottom-0 pt-4">
                <div className="flex w-full gap-2">
                  {stepControls.step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        stepControls.setStep(stepControls.step - 1)
                      }
                    >
                      Voltar
                    </Button>
                  )}

                  {stepControls.step < 2 && (
                    <Button
                      type="button"
                      className={cn("flex-1", colors.bg_primary)}
                      onClick={handleNextStep}
                    >
                      Próximo
                    </Button>
                  )}

                  {stepControls.step === 2 && (
                    <Button
                      type="submit"
                      className={cn("flex-1", colors.bg_primary)}
                      disabled={formState !== "default"}
                    >
                      {formState === "loading" && (
                        <>
                          Atualizando <LoaderSpin />
                        </>
                      )}
                      {formState === "default" && "Atualizar"}
                      {formState === "error" && "Erro ao atualizar"}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </Form>
          </ScrollArea>
        </FormSteps>
      </DialogContent>
    </Dialog>
  );
};
